/**
 * Extract GPS coordinates from a photo's EXIF metadata.
 *
 * Minimal EXIF parser — reads the JPEG APP1 segment, walks the TIFF IFD0
 * and GPS IFD tags, and returns { latitude, longitude } when both are
 * present. Returns null for images without EXIF GPS (PNG, screenshots,
 * photos where the camera stripped GPS, etc.).
 *
 * Runs entirely client-side; the file bytes never leave the browser.
 */

function readUint16(view, offset, littleEndian) {
  return view.getUint16(offset, littleEndian);
}

function readUint32(view, offset, littleEndian) {
  return view.getUint32(offset, littleEndian);
}

function readRational(view, offset, littleEndian) {
  const num = readUint32(view, offset, littleEndian);
  const den = readUint32(view, offset + 4, littleEndian);
  if (!den) return 0;
  return num / den;
}

function dmsToDecimal(view, offset, littleEndian) {
  // GPS coordinates are stored as three unsigned rationals: deg, min, sec.
  const deg = readRational(view, offset, littleEndian);
  const min = readRational(view, offset + 8, littleEndian);
  const sec = readRational(view, offset + 16, littleEndian);
  return deg + min / 60 + sec / 3600;
}

function readAscii(view, offset, count) {
  let out = '';
  for (let i = 0; i < count; i += 1) {
    const code = view.getUint8(offset + i);
    if (code === 0) break;
    out += String.fromCharCode(code);
  }
  return out;
}

export async function readExifGps(file) {
  if (!file) return null;
  if (file.type && !file.type.startsWith('image/jpeg')) {
    // Only JPEG carries the EXIF APP1 segment in a portable way. PNGs
    // can carry EXIF but few phone cameras write it there.
    return null;
  }
  const buffer = await file.slice(0, 128 * 1024).arrayBuffer();
  const view = new DataView(buffer);

  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null;

  let offset = 2;
  while (offset < view.byteLength) {
    if (view.getUint8(offset) !== 0xff) return null;
    const marker = view.getUint8(offset + 1);
    const size = view.getUint16(offset + 2);
    if (marker === 0xe1) {
      // APP1 — contains EXIF.
      const app1Start = offset + 4;
      const header = readAscii(view, app1Start, 4);
      if (header !== 'Exif') return null;
      const tiffStart = app1Start + 6;
      const byteOrder = view.getUint16(tiffStart);
      const littleEndian = byteOrder === 0x4949; // 'II'
      const magic = readUint16(view, tiffStart + 2, littleEndian);
      if (magic !== 0x002a) return null;
      const ifd0Offset = readUint32(view, tiffStart + 4, littleEndian);

      // Walk IFD0 looking for the GPS IFD pointer (tag 0x8825).
      const ifd0Start = tiffStart + ifd0Offset;
      const entries0 = readUint16(view, ifd0Start, littleEndian);
      let gpsIfdOffset = null;
      for (let i = 0; i < entries0; i += 1) {
        const entryOffset = ifd0Start + 2 + i * 12;
        const tag = readUint16(view, entryOffset, littleEndian);
        if (tag === 0x8825) {
          gpsIfdOffset = readUint32(view, entryOffset + 8, littleEndian);
          break;
        }
      }
      if (!gpsIfdOffset) return null;

      const gpsStart = tiffStart + gpsIfdOffset;
      const entriesG = readUint16(view, gpsStart, littleEndian);
      let latRef = null;
      let lat = null;
      let lonRef = null;
      let lon = null;
      for (let i = 0; i < entriesG; i += 1) {
        const entryOffset = gpsStart + 2 + i * 12;
        const tag = readUint16(view, entryOffset, littleEndian);
        const valueOffset = readUint32(view, entryOffset + 8, littleEndian);
        if (tag === 0x0001) {
          latRef = readAscii(view, entryOffset + 8, 1);
        } else if (tag === 0x0002) {
          lat = dmsToDecimal(view, tiffStart + valueOffset, littleEndian);
        } else if (tag === 0x0003) {
          lonRef = readAscii(view, entryOffset + 8, 1);
        } else if (tag === 0x0004) {
          lon = dmsToDecimal(view, tiffStart + valueOffset, littleEndian);
        }
      }
      if (lat == null || lon == null) return null;
      if (latRef === 'S') lat = -lat;
      if (lonRef === 'W') lon = -lon;
      if (!isFinite(lat) || !isFinite(lon)) return null;
      if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
      // Some cameras write 0/0 when they can't fix GPS — treat as absent.
      if (lat === 0 && lon === 0) return null;
      return { latitude: lat, longitude: lon };
    }
    if (size < 2) return null;
    offset += 2 + size;
  }
  return null;
}

export default readExifGps;
